import type { MyTenant } from "@/shared/types/auth";
import { GLOBAL_ROOTS, stripTenantBase } from "@/shared/lib/router/tenantPath";

export type TenantSlugAction =
    | { type: "none" }
    /** O slug da URL é de outra organização da pessoa; se a troca falhar, volta para `fallbackSlug`. */
    | { type: "switch"; tenantId: string; fallbackSlug: string | null }
    | { type: "redirect"; slug: string };

type TenantSlugInput = {
    urlSlug: string | null;
    pathname: string;
    tenants: MyTenant[];
    /** Ver `TenantRouting.isRoutableSlug`: sem esta guarda, um slug que a URL não reconhece recarregava sem fim. */
    isRoutableSlug: (slug: string) => boolean;
};

/**
 * Decide o que fazer quando a organização da URL e a da sessão divergem. Sem slug no
 * backend (organização atual sem `slug`), não faz nada: os endereços sem slug continuam válidos.
 */
export function resolveTenantSlugAction({ urlSlug, pathname, tenants, isRoutableSlug }: TenantSlugInput): TenantSlugAction {
    const current = tenants.find((tenant) => tenant.current);
    if (!current?.slug || urlSlug === current.slug) return { type: "none" };

    const currentSlug = isRoutableSlug(current.slug) ? current.slug : null;
    const target = urlSlug ? tenants.find((tenant) => tenant.slug === urlSlug) : undefined;
    if (target) return { type: "switch", tenantId: target.id, fallbackSlug: currentSlug };

    // Slug alheio (organização a que a pessoa não pertence) ou endereço sem slug: volta para a dela.
    const root = pathname.split("/")[1] ?? "";
    const isGlobalPage = !urlSlug && (GLOBAL_ROOTS as readonly string[]).includes(root);
    return currentSlug && !isGlobalPage ? { type: "redirect", slug: currentSlug } : { type: "none" };
}

/** Mesmo caminho, query e hash, mas debaixo de outra organização. */
export function pathUnderTenant(slug: string, location: Pick<Location, "pathname" | "search" | "hash">, urlSlug: string | null): string {
    const inner = stripTenantBase(location.pathname, urlSlug);
    return `/${slug}${inner === "/" ? "/" : inner}${location.search}${location.hash}`;
}
